import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { breathState } from "../../lib/breath";

const LAV = "#A47BBE";
const ROSE = "#E39BBE";
const FACE =
  "M31 94 C31 82 27 73 25 63 C19 41 27 17 48 12 C64 9 74 20 74 32 C74 36 76 39 79 44 L84 52 C85 54 83 55 80.5 55.5 C81.5 57 82 58 80.5 59.6 C82 60.6 81.6 62.6 79.6 63.6 C78.4 67 79 70 74 72 C70 73 66 72 64.5 76 C63.5 82 64.5 88 66.5 94";
const HAIR = "M48 12 C30 13 14 30 18 52 C20 65 13 77 8 85 M30 22 C24 30 23 40 26 48";
const EYE = { open: "M61 40 Q65 37.4 69 40", shut: "M61 40.4 Q65 43 69 40.4" };
const SMILE = { rest: "M73.6 62.6 Q75.6 62.7 77.6 62.2", soft: "M73.4 62 Q75.6 64.4 78 61.5" };
const CHEEK = { rest: "M64 50.5 Q67 52.6 70 50.8", puff: "M63 50 Q69.5 56.5 73.5 50.2" };
const STEPS = [["smile", 700], ["close", 500], ["inhale", 900], ["blow", 1400], ["rest", 1200]];
const LOOPS = 3;
const MOUTH = { x: 0.86, y: 0.6 };
const BUBBLES = [
  { r: 1.6, tx: 34, ty: -16, d: 0.15 },
  { r: 2.4, tx: 48, ty: -22, d: 0.4 },
  { r: 1.2, tx: 62, ty: -32, d: 0.65 },
];

function useDemo(active, slow, onBlow) {
  const [step, setStep] = useState({ name: "draw", loop: 0 });
  const cb = useRef(onBlow);
  cb.current = onBlow;
  useEffect(() => {
    if (!active) return;
    const k0 = slow ? 1.5 : 1;
    let id, k = -1, loop = 0;
    const next = () => {
      k++;
      if (k === STEPS.length) (k = 0), loop++;
      if (loop >= LOOPS) return setStep({ name: "idle", loop });
      const [name, ms] = STEPS[k];
      setStep({ name, loop });
      if (name === "blow") cb.current?.();
      id = setTimeout(next, ms * k0);
    };
    id = setTimeout(next, slow ? 900 : 1900);
    return () => clearTimeout(id);
  }, [active, slow]);
  return step;
}

function face(mic, step) {
  if (mic === "blown") return { eye: "open", smile: "soft", cheek: "rest", o: false };
  if (mic === "listening" || mic === "asking") return { eye: "shut", smile: "rest", cheek: "rest", o: false };
  const n = step.name;
  return {
    eye: n === "close" || n === "inhale" || n === "blow" ? "shut" : "open",
    smile: n === "smile" || n === "close" ? "soft" : "rest",
    cheek: n === "inhale" ? "puff" : "rest",
    o: n === "blow",
  };
}

function Drawn({ d, width, draw, testid }) {
  const ref = useRef(null);
  const [len, setLen] = useState(0);
  useLayoutEffect(() => {
    if (draw && ref.current?.getTotalLength) setLen(Math.ceil(ref.current.getTotalLength()) + 2);
  }, [draw]);
  const style = draw && len ? { strokeDasharray: len, strokeDashoffset: len, "--len": len, animation: "muse-draw 1.5s ease-in-out 0.3s forwards" } : undefined;
  return <path ref={ref} data-testid={testid} d={d} stroke={LAV} strokeWidth={width} fill="none" strokeLinecap="round" strokeLinejoin="round" style={style} />;
}

function Fade({ d, on, color, width, testid, extra }) {
  return (
    <path
      data-testid={testid}
      d={d}
      stroke={color}
      strokeWidth={width}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ opacity: on ? 1 : 0, transition: "opacity 0.45s ease-in-out" }}
      {...extra}
    />
  );
}

function Bubbles({ cycle, slow }) {
  return (
    <g key={cycle} data-testid="breath-muse-bubbles">
      {BUBBLES.map((b, i) => (
        <circle
          key={i}
          cx={87}
          cy={60}
          r={b.r}
          fill="none"
          stroke={ROSE}
          strokeWidth={0.9}
          style={{ opacity: 0, "--tx": `${b.tx}px`, "--ty": `${b.ty}px`, animation: `muse-bubble ${slow ? 2.1 : 1.5}s ease-out ${b.d}s forwards` }}
        />
      ))}
    </g>
  );
}

function Halo({ on }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!on) return;
    let raf, v = 0;
    const loop = (now) => {
      v += (Math.max(breathState.level, breathState.pre * 0.55) - v) * 0.25;
      if (ref.current) {
        ref.current.style.transform = `scale(${1 + 0.08 * Math.sin(now / 900) + v * 1.8})`;
        ref.current.style.opacity = String(0.3 + v * 0.6);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [on]);
  return on ? <span ref={ref} data-testid="breath-halo" className="pointer-events-none absolute left-[62%] top-[44%] h-12 w-12 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.95)_0%,rgba(227,155,190,0.45)_45%,rgba(255,214,234,0)_72%)]" /> : null;
}

const CAPTION = { idle: "play with the bubbles by blowing softly on them", asking: "breathe out gently…", listening: "breathe out gently…", blown: "there they go…", denied: "or swipe softly" };

export function BreathMuse({ mic, onStart, onDemoBlow }) {
  const reduced = !!useReducedMotion();
  const btn = useRef(null);
  const [gone, setGone] = useState(false);
  const step = useDemo(mic === "idle", reduced, () => {
    const r = btn.current?.getBoundingClientRect();
    if (r) onDemoBlow(r.left + r.width * MOUTH.x, r.top + r.height * MOUTH.y);
  });
  useEffect(() => {
    if (mic !== "blown") return;
    const id = setTimeout(() => setGone(true), 1800);
    return () => clearTimeout(id);
  }, [mic]);
  const f = face(mic, step);
  const live = mic === "listening" || mic === "blown";
  return (
    <AnimatePresence>
      {!gone && (
        <motion.div
          key="muse"
          data-testid="breath-muse"
          data-state={mic}
          data-step={step.name}
          data-loop={step.loop}
          data-reduced={reduced ? "true" : "false"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 1.6 } }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="pointer-events-auto absolute bottom-[calc(env(safe-area-inset-bottom)+40px)] left-1/2 z-30 flex -translate-x-1/2 items-end gap-1 sm:bottom-6 sm:left-6 sm:translate-x-0"
        >
          <button
            ref={btn}
            data-testid="breath-muse-button"
            onClick={mic === "idle" ? onStart : undefined}
            aria-label="Breathe softly on the bubbles. Your breath stays with you."
            title="your breath stays with you"
            className="relative h-[96px] w-[96px] shrink-0 rounded-full transition-transform duration-500 hover:scale-105 sm:h-[110px] sm:w-[110px]"
            style={step.name === "idle" || live ? { animation: "muse-breathe 4.2s ease-in-out infinite" } : undefined}
          >
            <Halo on={live} />
            <svg viewBox="0 0 100 100" className="h-full w-full overflow-visible drop-shadow-[0_0_6px_rgba(255,255,255,0.9)]">
              <Drawn d={HAIR} width={1.7} draw={!reduced} />
              <Drawn d={FACE} width={2.1} draw={!reduced} testid="breath-muse-face" />
              <g data-testid="breath-muse-eye" data-eye={f.eye}>
                <Fade d={EYE.open} on={f.eye === "open"} color={LAV} width={1.8} />
                <Fade d={EYE.shut} on={f.eye === "shut"} color={LAV} width={1.8} />
              </g>
              <Fade d={SMILE.rest} on={f.smile === "rest" && !f.o} color={ROSE} width={1.6} />
              <Fade d={SMILE.soft} on={f.smile === "soft"} color={ROSE} width={1.6} />
              <g data-testid="breath-muse-cheek" data-cheek={f.cheek}>
                <Fade d={CHEEK.rest} on={f.cheek === "rest"} color={ROSE} width={1} extra={{ strokeOpacity: 0.35 }} />
                <Fade d={CHEEK.puff} on={f.cheek === "puff"} color={ROSE} width={1.1} />
              </g>
              <circle data-testid="breath-muse-pucker" cx={83.4} cy={60} r={1.6} fill="none" stroke={ROSE} strokeWidth={1.1} style={{ opacity: f.o ? 1 : 0, transition: "opacity 0.3s ease" }} />
              {f.o && <Bubbles cycle={step.loop} slow={reduced} />}
              {mic === "denied" && (
                <path
                  data-testid="breath-muse-swipe"
                  d="M92 84 C104 78 116 76 128 70 M123 67 L128 70 L124 75"
                  fill="none"
                  stroke={ROSE}
                  strokeWidth={1.3}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ strokeDasharray: 60, strokeDashoffset: 60, animation: "muse-swipe 2.8s ease-in-out infinite" }}
                />
              )}
            </svg>
          </button>
          <span className="mb-3 flex flex-col leading-tight">
            <span key={mic} data-testid="breath-muse-caption" className="font-hand text-[17px] text-[#7A6690]" style={{ animation: "muse-fade 0.8s ease-out both" }}>
              {CAPTION[mic]}
            </span>
            {mic !== "denied" && (
              <span data-testid="blow-privacy-note" className="font-hand text-[11px] text-[#9C8AAE]/75">
                your breath stays with you
              </span>
            )}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
